import React from 'react';
import { ANALYTICS_EVENT, ANALYTICS_PROPERTY } from '@kouto/types';
import { analyticsManager } from 'features/analytics';
import {
  recommendationAnalyticsProduct,
  RECOMMENDATIONS_LIST_NAME,
} from 'features/analytics/utils/recommendationAnalytics';
import { FeaturedListingV3 } from 'types/featuredListingV3';
import { GenericListingCard } from 'components/atoms/GenericListingCard';
import getCoverImage from 'utils/data/medias/getCoverImage';
import { getListingRoute } from 'utils/listings';
import useListingHref from 'hooks/useListingHref';
import { useHistory } from 'react-router-dom';
import { useFormatPrice } from 'utils/listingsHelpers';

interface BookingConfirmationCardProps {
  listing: FeaturedListingV3;
  recommendationId?: string | null;
  /** Position in the row, for the click event's product index. */
  index?: number;
  /** Which list the card sits in; the click event carries it as the product's itemListName. */
  analyticsListName?: string;
}

const BookingConfirmationCard = ({
  listing,
  recommendationId,
  index = 0,
  analyticsListName = RECOMMENDATIONS_LIST_NAME,
}: BookingConfirmationCardProps) => {
  const listingRoute = getListingRoute(listing);
  const listingHref = useListingHref(listingRoute);
  const history = useHistory();

  const priceDisplay = useFormatPrice({
    advertisedPrice: listing.advertisedPrice,
    bookingAvailabilityMode: listing.bookingAvailabilityMode,
    maxParticipants: listing.maxParticipants,
    startingPrice: listing.startingPrice,
  });

  const handleNavigation = () => {
    history.push(listingRoute);
  };

  const onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const isNewTabIntent = e.metaKey || e.ctrlKey || e.button === 1;

    // Same event the catalog tiles fire, so a recommendation click joins the existing funnel.
    analyticsManager.sendEvent(ANALYTICS_EVENT.CLICK_EXPERIENCE, {
      [ANALYTICS_PROPERTY.ExperienceId]: listing.experienceId ?? listing.id,
      [ANALYTICS_PROPERTY.ExperienceTitle]: listing.title ?? undefined,
      [ANALYTICS_PROPERTY.ExperienceCategory]:
        listing.category?.name ?? undefined,
      [ANALYTICS_PROPERTY.Products]: JSON.stringify([
        recommendationAnalyticsProduct(listing, index, analyticsListName),
      ]),
    });

    window.sessionStorage.setItem(
      'selected_recommended_listing',
      JSON.stringify({
        id: listing.id,
        experienceId: listing.experienceId,
        resourceGroupCollectionId: listing.resourceGroupCollectionId,
        title: listing.title,
        recommendationId,
      }),
    );

    if (isNewTabIntent) {
      return;
    }

    e.preventDefault();
    handleNavigation();
  };

  return (
    <a href={listingHref} onClick={onClick} style={{ textDecoration: 'none' }}>
      <GenericListingCard
        title={listing.title ?? ''}
        image={getCoverImage(listing.medias, '1080')?.url ?? ''}
        description={priceDisplay}
      />
    </a>
  );
};

export default BookingConfirmationCard;
